import { useEffect, useState } from 'react';
import type { EmailProvider, TranscriptionProvider } from '@klaro7/shared-types';
import {
  Badge,
  Button,
  Input,
  Spinner,
} from '@klaro7/ui';

import {
  useServicesSettings,
  useUpdateServicesSettings,
  useOpenAIModels,
  useGeminiModels,
  useTestSmtp,
  useTestOpenAIChat,
  useTestOpenAIWhisper,
  useTestGeminiTranscription,
} from '@/features/settings/hooks/useServicesSettings';
import { FeatureFlagsSection } from '@/features/settings/components/FeatureFlagsSection';

export function AdminServicesPage() {
  const { data: settings, isLoading } = useServicesSettings();
  const updateMutation = useUpdateServicesSettings();
  const smtpTestMutation = useTestSmtp();
  const openaiChatTestMutation = useTestOpenAIChat();
  const openaiWhisperTestMutation = useTestOpenAIWhisper();
  const geminiTranscriptionTestMutation = useTestGeminiTranscription();

  // OpenAI form state
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('gpt-4o');
  const [openaiEnabled, setOpenaiEnabled] = useState(false);
  const [loadModels, setLoadModels] = useState(false);

  // Gemini form state
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash');
  const [geminiEnabled, setGeminiEnabled] = useState(false);
  const [loadGeminiModels, setLoadGeminiModels] = useState(false);

  // Transcription provider
  const [transcriptionProvider, setTranscriptionProvider] = useState<TranscriptionProvider>('openai');

  // Has a saved (masked) API key?
  const hasSavedApiKey = settings?.openai.apiKey ? settings.openai.apiKey.startsWith('****') && settings.openai.apiKey.length > 4 : false;
  const hasSavedGeminiKey = settings?.gemini?.apiKey ? settings.gemini.apiKey.startsWith('****') && settings.gemini.apiKey.length > 4 : false;

  const {
    data: openaiModels,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useOpenAIModels(loadModels && hasSavedApiKey);

  const {
    data: geminiModels,
    isLoading: isLoadingGeminiModels,
    error: geminiModelsError,
  } = useGeminiModels(loadGeminiModels && hasSavedGeminiKey);

  // Email provider
  const [activeProvider, setActiveProvider] = useState<EmailProvider>('none');

  // SES form state
  const [sesAccessKeyId, setSesAccessKeyId] = useState('');
  const [sesSecretAccessKey, setSesSecretAccessKey] = useState('');
  const [sesRegion, setSesRegion] = useState('eu-south-1');
  const [sesFromEmail, setSesFromEmail] = useState('');
  const [sesEnabled, setSesEnabled] = useState(false);

  // SMTP form state
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpSecure, setSmtpSecure] = useState(true);
  const [smtpEnabled, setSmtpEnabled] = useState(false);

  // Platform form state
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);

  // SMTP test state
  const [smtpTestResult, setSmtpTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  // OpenAI test state
  const [openaiChatTestResult, setOpenaiChatTestResult] = useState<{ ok: boolean; response?: string; model?: string; error?: string } | null>(null);
  const [openaiWhisperTestResult, setOpenaiWhisperTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  // Gemini test state
  const [geminiTranscriptionTestResult, setGeminiTranscriptionTestResult] = useState<{ ok: boolean; model?: string; error?: string } | null>(null);

  // Toast state
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string; detail?: string } | null>(null);

  // Populate form when settings load
  useEffect(() => {
    if (settings) {
      setOpenaiApiKey(settings.openai.apiKey);
      setOpenaiModel(settings.openai.model);
      setOpenaiEnabled(settings.openai.enabled);

      setGeminiApiKey(settings.gemini?.apiKey ?? '');
      setGeminiModel(settings.gemini?.model ?? 'gemini-2.5-flash');
      setGeminiEnabled(settings.gemini?.enabled ?? false);

      setTranscriptionProvider(settings.transcription?.activeProvider ?? 'openai');

      setActiveProvider(settings.email.activeProvider);

      setSesAccessKeyId(settings.email.ses.accessKeyId);
      setSesSecretAccessKey(settings.email.ses.secretAccessKey);
      setSesRegion(settings.email.ses.region);
      setSesFromEmail(settings.email.ses.fromEmail);
      setSesEnabled(settings.email.ses.enabled);

      setSmtpHost(settings.email.smtp.host);
      setSmtpPort(settings.email.smtp.port);
      setSmtpUsername(settings.email.smtp.username);
      setSmtpPassword(settings.email.smtp.password);
      setSmtpFromEmail(settings.email.smtp.fromEmail);
      setSmtpSecure(settings.email.smtp.secure);
      setSmtpEnabled(settings.email.smtp.enabled);

      setRegistrationEnabled(settings.platform?.registrationEnabled ?? false);
      setWaitlistEnabled(settings.platform?.waitlistEnabled ?? false);
    }
  }, [settings]);

  // Auto-dismiss toast (4s success, 15s error)
  useEffect(() => {
    if (!toast) return;
    const duration = toast.type === 'error' ? 15000 : 4000;
    const timer = setTimeout(() => setToast(null), duration);
    return () => clearTimeout(timer);
  }, [toast]);

  function showSuccess(message: string) {
    setToast({ type: 'success', message });
  }

  function showError(message: string, detail?: string) {
    setToast({ type: 'error', message, detail });
  }

  function extractErrorDetail(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
      const resp = err as { response?: { data?: { error?: { message?: string }; message?: string }; status?: number; statusText?: string } };
      const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
      const status = resp.response?.status;
      if (apiMsg) return `[${status}] ${apiMsg}`;
      if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
    }
    if (err instanceof Error) return err.message;
    return String(err);
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).catch(() => {});
  }

  async function handleSaveOpenAI() {
    try {
      await updateMutation.mutateAsync({
        openai: {
          apiKey: openaiApiKey || undefined,
          model: openaiModel,
          enabled: openaiEnabled,
        },
      });
      showSuccess('Impostazioni OpenAI salvate con successo');
    } catch (err) {
      showError('Errore nel salvataggio delle impostazioni OpenAI', extractErrorDetail(err));
    }
  }

  async function handleTestOpenAIChat() {
    setOpenaiChatTestResult(null);
    try {
      const result = await openaiChatTestMutation.mutateAsync();
      setOpenaiChatTestResult({ ok: result.success, response: result.response, model: result.model, error: result.error });
    } catch (err: unknown) {
      const detail = extractErrorDetail(err);
      setOpenaiChatTestResult({ ok: false, error: detail });
    }
  }

  async function handleTestOpenAIWhisper() {
    setOpenaiWhisperTestResult(null);
    try {
      const result = await openaiWhisperTestMutation.mutateAsync();
      setOpenaiWhisperTestResult({ ok: result.success, error: result.error });
    } catch (err: unknown) {
      const detail = extractErrorDetail(err);
      setOpenaiWhisperTestResult({ ok: false, error: detail });
    }
  }

  async function handleSaveGemini() {
    try {
      await updateMutation.mutateAsync({
        gemini: {
          apiKey: geminiApiKey || undefined,
          model: geminiModel,
          enabled: geminiEnabled,
        },
      });
      showSuccess('Impostazioni Gemini salvate con successo');
    } catch (err) {
      showError('Errore nel salvataggio delle impostazioni Gemini', extractErrorDetail(err));
    }
  }

  async function handleSaveTranscription() {
    try {
      await updateMutation.mutateAsync({
        transcription: {
          activeProvider: transcriptionProvider,
        },
      });
      showSuccess('Provider trascrizione aggiornato');
    } catch (err) {
      showError('Errore nel salvataggio del provider trascrizione', extractErrorDetail(err));
    }
  }

  async function handleTestGeminiTranscription() {
    setGeminiTranscriptionTestResult(null);
    try {
      const result = await geminiTranscriptionTestMutation.mutateAsync();
      setGeminiTranscriptionTestResult({ ok: result.success, model: result.model, error: result.error });
    } catch (err: unknown) {
      const detail = extractErrorDetail(err);
      setGeminiTranscriptionTestResult({ ok: false, error: detail });
    }
  }

  async function handleTestSmtp() {
    setSmtpTestResult(null);
    try {
      const result = await smtpTestMutation.mutateAsync();
      setSmtpTestResult({ ok: result.success, error: result.error });
    } catch (err: unknown) {
      const detail = extractErrorDetail(err);
      setSmtpTestResult({ ok: false, error: detail });
    }
  }

  async function handleSaveEmail() {
    try {
      await updateMutation.mutateAsync({
        email: {
          activeProvider,
          ses: {
            accessKeyId: sesAccessKeyId || undefined,
            secretAccessKey: sesSecretAccessKey || undefined,
            region: sesRegion,
            fromEmail: sesFromEmail,
            enabled: sesEnabled,
          },
          smtp: {
            host: smtpHost,
            port: smtpPort,
            username: smtpUsername,
            password: smtpPassword || undefined,
            fromEmail: smtpFromEmail,
            secure: smtpSecure,
            enabled: smtpEnabled,
          },
        },
      });
      showSuccess('Impostazioni Email salvate con successo');
    } catch (err) {
      showError('Errore nel salvataggio delle impostazioni Email', extractErrorDetail(err));
    }
  }

  async function handleSavePlatform() {
    try {
      await updateMutation.mutateAsync({
        platform: {
          registrationEnabled,
          waitlistEnabled,
        },
      });
      showSuccess('Impostazioni piattaforma salvate con successo');
    } catch (err) {
      showError('Errore nel salvataggio delle impostazioni piattaforma', extractErrorDetail(err));
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const providerLabels = [
    { value: 'none' as const, label: 'Nessuno', description: 'Email disattivate' },
    { value: 'ses' as const, label: 'Amazon SES', description: 'Servizio email scalabile' },
    { value: 'smtp' as const, label: 'SMTP', description: 'Server email tradizionale' },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Servizi Esterni</h1>
        <p className="mt-1 text-sm text-neutral-600">Configura le integrazioni con servizi esterni</p>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-lg animate-slide-down rounded-xl border shadow-lg ${
          toast.type === 'success'
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-red-200 bg-red-50'
        }`}>
          <div className="flex items-start gap-3 p-4">
            {toast.type === 'success' ? (
              <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${toast.type === 'success' ? 'text-emerald-800' : 'text-red-800'}`}>
                {toast.message}
              </p>
              {toast.detail && (
                <p className="mt-1 text-xs text-red-700 break-all font-mono bg-red-100 rounded px-2 py-1.5">
                  {toast.detail}
                </p>
              )}
              {toast.type === 'error' && toast.detail && (
                <button
                  onClick={() => copyToClipboard(`${toast.message}\n${toast.detail}`)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-200 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copia errore
                </button>
              )}
            </div>
            <button
              onClick={() => setToast(null)}
              className={`flex-shrink-0 rounded-lg p-1 transition-colors ${
                toast.type === 'success' ? 'text-emerald-500 hover:bg-emerald-100' : 'text-red-500 hover:bg-red-100'
              }`}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div className={`rounded-xl border border-neutral-200 bg-white p-5 border-l-4 ${registrationEnabled ? 'border-l-emerald-500' : 'border-l-red-400'}`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${registrationEnabled ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <svg className={`h-5 w-5 ${registrationEnabled ? 'text-emerald-600' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">Registrazioni</p>
              <Badge variant={registrationEnabled ? 'success' : 'error'} className="mt-0.5">
                {registrationEnabled ? 'Abilitate' : 'Disabilitate'}
              </Badge>
            </div>
          </div>
        </div>
        <div className={`rounded-xl border border-neutral-200 bg-white p-5 border-l-4 ${openaiEnabled ? 'border-l-brand-teal' : 'border-l-neutral-300'}`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${openaiEnabled ? 'bg-brand-teal-50' : 'bg-neutral-100'}`}>
              <svg className={`h-5 w-5 ${openaiEnabled ? 'text-brand-teal-600' : 'text-neutral-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">OpenAI</p>
              <Badge variant={openaiEnabled ? 'success' : 'default'} className="mt-0.5">
                {openaiEnabled ? 'Attivo' : 'Disattivato'}
              </Badge>
            </div>
          </div>
        </div>
        <div className={`rounded-xl border border-neutral-200 bg-white p-5 border-l-4 ${geminiEnabled ? 'border-l-indigo-500' : 'border-l-neutral-300'}`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${geminiEnabled ? 'bg-indigo-50' : 'bg-neutral-100'}`}>
              <svg className={`h-5 w-5 ${geminiEnabled ? 'text-indigo-600' : 'text-neutral-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">Gemini</p>
              <Badge variant={geminiEnabled ? 'success' : 'default'} className="mt-0.5">
                {geminiEnabled ? 'Attivo' : 'Disattivato'}
              </Badge>
            </div>
          </div>
        </div>
        <div className={`rounded-xl border border-neutral-200 bg-white p-5 border-l-4 ${sesEnabled ? 'border-l-brand-orange' : 'border-l-neutral-300'}`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${sesEnabled ? 'bg-brand-orange-50' : 'bg-neutral-100'}`}>
              <svg className={`h-5 w-5 ${sesEnabled ? 'text-brand-orange-600' : 'text-neutral-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">Amazon SES</p>
              <Badge variant={sesEnabled ? 'success' : 'default'} className="mt-0.5">
                {sesEnabled ? 'Attivo' : 'Disattivato'}
              </Badge>
            </div>
          </div>
        </div>
        <div className={`rounded-xl border border-neutral-200 bg-white p-5 border-l-4 ${smtpEnabled ? 'border-l-brand-navy' : 'border-l-neutral-300'}`}>
          <div className="flex items-center gap-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${smtpEnabled ? 'bg-brand-navy-50' : 'bg-neutral-100'}`}>
              <svg className={`h-5 w-5 ${smtpEnabled ? 'text-brand-navy' : 'text-neutral-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-900">SMTP</p>
              <Badge variant={smtpEnabled ? 'success' : 'default'} className="mt-0.5">
                {smtpEnabled ? 'Attivo' : 'Disattivato'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Section */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">Piattaforma</h3>
              <p className="text-xs text-neutral-600">Impostazioni generali della piattaforma</p>
            </div>
          </div>
          <Badge variant={registrationEnabled ? 'success' : 'error'}>
            {registrationEnabled ? 'Registrazioni abilitate' : 'Registrazioni disabilitate'}
          </Badge>
        </div>
        <div className="space-y-4 p-5">
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 p-4">
            <div>
              <p className="text-sm font-medium text-neutral-900">Abilita registrazioni utenti</p>
              <p className="text-xs text-neutral-600 mt-1">
                Quando disabilitato, la pagina <span className="font-medium">/register</span> mostra un messaggio
                &quot;Registrazioni temporaneamente chiuse&quot; (nessun redirect esterno; la landing è dismessa)
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={registrationEnabled}
              onClick={() => setRegistrationEnabled(!registrationEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 ${
                registrationEnabled ? 'bg-brand-teal' : 'bg-neutral-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  registrationEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-neutral-200 p-4">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-neutral-900">Lista d'attesa</p>
                <Badge variant={waitlistEnabled ? 'success' : 'error'}>
                  {waitlistEnabled ? 'Attiva' : 'Chiusa'}
                </Badge>
              </div>
              <p className="text-xs text-neutral-600 mt-1">
                Quando disattivata, l&apos;iscrizione alla lista d&apos;attesa (form di registrazione e popup) risponde con
                errore e non accetta nuove iscrizioni.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={waitlistEnabled}
              onClick={() => setWaitlistEnabled(!waitlistEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 ${
                waitlistEnabled ? 'bg-brand-teal' : 'bg-neutral-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  waitlistEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="flex justify-end border-t border-neutral-100 pt-4">
            <Button
              onClick={handleSavePlatform}
              loading={updateMutation.isPending}
            >
              Salva Piattaforma
            </Button>
          </div>
        </div>
      </div>

      {/* OpenAI Section */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">OpenAI</h3>
              <p className="text-xs text-neutral-600">Configurazione API OpenAI per i servizi AI</p>
            </div>
          </div>
          <Badge variant={openaiEnabled ? 'success' : 'default'}>
            {openaiEnabled ? 'Attivo' : 'Disattivato'}
          </Badge>
        </div>
        <div className="space-y-4 p-5">
          <Input
            label="API Key"
            type="password"
            value={openaiApiKey}
            onChange={(e) => setOpenaiApiKey(e.target.value)}
            placeholder="sk-..."
          />
          <div className="w-full">
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              Modello
            </label>
            {openaiModels && openaiModels.length > 0 ? (
              <select
                value={openaiModel}
                onChange={(e) => setOpenaiModel(e.target.value)}
                className="flex h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-colors duration-200"
              >
                {!openaiModels.some((m) => m.id === openaiModel) && (
                  <option value={openaiModel}>{openaiModel}</option>
                )}
                {openaiModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={openaiModel}
                  readOnly
                  className="flex h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-900 cursor-default"
                />
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => setLoadModels(true)}
                  loading={isLoadingModels}
                  disabled={!hasSavedApiKey}
                  title={!hasSavedApiKey ? 'Salva prima la API Key' : 'Carica modelli da OpenAI'}
                >
                  {isLoadingModels ? 'Caricamento...' : 'Carica modelli'}
                </Button>
              </div>
            )}
            {modelsError && (
              <p className="mt-1.5 text-sm text-error">
                {!hasSavedApiKey
                  ? 'Salva prima la API Key per caricare i modelli'
                  : 'Errore nel caricamento dei modelli. Verifica la API Key.'}
              </p>
            )}
            {!hasSavedApiKey && !modelsError && (
              <p className="mt-1.5 text-sm text-neutral-600">
                Salva la API Key per poter selezionare i modelli disponibili
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={openaiEnabled}
              onChange={(e) => setOpenaiEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
            />
            Abilitato
          </label>

          {/* Test buttons */}
          <div className="space-y-3 border-t border-neutral-100 pt-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Verifica Servizi</p>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestOpenAIChat}
                  loading={openaiChatTestMutation.isPending}
                  disabled={!hasSavedApiKey}
                  title={!hasSavedApiKey ? 'Salva prima la API Key' : 'Verifica modello chat'}
                >
                  Verifica Modello Chat
                </Button>
                {openaiChatTestResult && (
                  <div className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                    openaiChatTestResult.ok
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {openaiChatTestResult.ok ? (
                      <>
                        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>OK — {openaiChatTestResult.model} risponde: "{openaiChatTestResult.response}"</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>KO — {openaiChatTestResult.error}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestOpenAIWhisper}
                  loading={openaiWhisperTestMutation.isPending}
                  disabled={!hasSavedApiKey}
                  title={!hasSavedApiKey ? 'Salva prima la API Key' : 'Verifica trascrizione Whisper'}
                >
                  Verifica Trascrizione
                </Button>
                {openaiWhisperTestResult && (
                  <div className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                    openaiWhisperTestResult.ok
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {openaiWhisperTestResult.ok ? (
                      <>
                        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>OK — Whisper funzionante</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>KO — {openaiWhisperTestResult.error}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t border-neutral-100 pt-4">
            <Button
              onClick={handleSaveOpenAI}
              loading={updateMutation.isPending}
            >
              Salva OpenAI
            </Button>
          </div>
        </div>
      </div>

      {/* Gemini Section */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between rounded-t-xl border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">Google Gemini</h3>
              <p className="text-xs text-neutral-600">
                Configurazione API Gemini per trascrizione audio
                {geminiModel && <span className="ml-1 font-medium text-indigo-600">({geminiModel})</span>}
              </p>
            </div>
          </div>
          <Badge variant={geminiEnabled ? 'success' : 'default'}>
            {geminiEnabled ? 'Attivo' : 'Disattivato'}
          </Badge>
        </div>
        <div className="space-y-4 p-5">
          <Input
            label="API Key"
            type="password"
            value={geminiApiKey}
            onChange={(e) => setGeminiApiKey(e.target.value)}
            placeholder="AIza..."
          />
          <div className="w-full">
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              Modello
            </label>
            {geminiModels && geminiModels.length > 0 ? (
              <select
                value={geminiModel}
                onChange={(e) => setGeminiModel(e.target.value)}
                className="flex h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-colors duration-200"
              >
                {!geminiModels.some((m) => m.id === geminiModel) && (
                  <option value={geminiModel}>{geminiModel}</option>
                )}
                {geminiModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id} — {model.displayName}
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={geminiModel}
                  readOnly
                  className="flex h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-900 cursor-default"
                />
                <button
                  type="button"
                  onClick={() => setLoadGeminiModels(true)}
                  disabled={!hasSavedGeminiKey || isLoadingGeminiModels}
                  title={!hasSavedGeminiKey ? 'Salva prima la API Key' : 'Carica modelli da Gemini'}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-brand-teal disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isLoadingGeminiModels ? (
                    <Spinner size="sm" />
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                    </svg>
                  )}
                </button>
              </div>
            )}
            {geminiModelsError && (
              <p className="mt-1.5 text-sm text-error">
                {!hasSavedGeminiKey
                  ? 'Salva prima la API Key per caricare i modelli'
                  : 'Errore nel caricamento dei modelli. Verifica la API Key.'}
              </p>
            )}
            {!hasSavedGeminiKey && !geminiModelsError && (
              <p className="mt-1.5 text-sm text-neutral-600">
                Salva la API Key per poter selezionare i modelli disponibili
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={geminiEnabled}
              onChange={(e) => setGeminiEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
            />
            Abilitato
          </label>

          {/* Test button */}
          <div className="space-y-3 border-t border-neutral-100 pt-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Verifica Servizi</p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestGeminiTranscription}
                loading={geminiTranscriptionTestMutation.isPending}
              >
                Verifica Trascrizione
              </Button>
              {geminiTranscriptionTestResult && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  geminiTranscriptionTestResult.ok
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {geminiTranscriptionTestResult.ok ? (
                    <>
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>OK — Modello: {geminiTranscriptionTestResult.model || 'gemini-2.5-flash'}</span>
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>KO — {geminiTranscriptionTestResult.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end border-t border-neutral-100 pt-4">
            <Button
              onClick={handleSaveGemini}
              loading={updateMutation.isPending}
            >
              Salva Gemini
            </Button>
          </div>
        </div>
      </div>

      {/* Transcription Provider Selection */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Provider Trascrizione Attivo</h3>
            <p className="text-xs text-neutral-600">Seleziona il servizio per la trascrizione delle videochiamate</p>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {([
              { value: 'openai' as const, label: 'OpenAI Whisper', description: 'Trascrizione tramite Whisper API', enabled: openaiEnabled && hasSavedApiKey },
              { value: 'gemini' as const, label: 'Google Gemini', description: 'Trascrizione tramite Gemini API', enabled: geminiEnabled && hasSavedGeminiKey },
            ]).map((provider) => (
              <label
                key={provider.value}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all ${
                  transcriptionProvider === provider.value
                    ? 'border-brand-teal bg-brand-teal-50/50'
                    : 'border-neutral-200 hover:border-neutral-300'
                } ${!provider.enabled ? 'opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name="transcriptionProvider"
                  value={provider.value}
                  checked={transcriptionProvider === provider.value}
                  onChange={() => setTranscriptionProvider(provider.value)}
                  className="h-4 w-4 border-neutral-300 text-brand-teal focus:ring-brand-teal"
                />
                <div>
                  <p className="text-sm font-medium text-neutral-900">{provider.label}</p>
                  <p className="text-xs text-neutral-600">{provider.description}</p>
                  {!provider.enabled && (
                    <p className="text-xs text-amber-600 mt-1">Configura e abilita prima il servizio</p>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end border-t border-neutral-100 pt-4 mt-4">
            <Button
              onClick={handleSaveTranscription}
              loading={updateMutation.isPending}
            >
              Salva Provider Trascrizione
            </Button>
          </div>
        </div>
      </div>

      {/* Email Provider Selection */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Provider Email Attivo</h3>
            <p className="text-xs text-neutral-600">Seleziona il provider per l'invio email</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-5">
          {providerLabels.map((provider) => (
            <label
              key={provider.value}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 transition-all ${
                activeProvider === provider.value
                  ? 'border-brand-teal bg-brand-teal-50/50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <input
                type="radio"
                name="emailProvider"
                value={provider.value}
                checked={activeProvider === provider.value}
                onChange={() => setActiveProvider(provider.value)}
                className="h-4 w-4 border-neutral-300 text-brand-teal focus:ring-brand-teal"
              />
              <div>
                <p className="text-sm font-medium text-neutral-900">{provider.label}</p>
                <p className="text-xs text-neutral-600">{provider.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Amazon SES Section */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">Amazon SES</h3>
              <p className="text-xs text-neutral-600">Configurazione Amazon Simple Email Service</p>
            </div>
          </div>
          <Badge variant={sesEnabled ? 'success' : 'default'}>
            {sesEnabled ? 'Attivo' : 'Disattivato'}
          </Badge>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Access Key ID"
              type="password"
              value={sesAccessKeyId}
              onChange={(e) => setSesAccessKeyId(e.target.value)}
              placeholder="AKIA..."
            />
            <Input
              label="Secret Access Key"
              type="password"
              value={sesSecretAccessKey}
              onChange={(e) => setSesSecretAccessKey(e.target.value)}
              placeholder="..."
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Regione"
              value={sesRegion}
              onChange={(e) => setSesRegion(e.target.value)}
              placeholder="eu-south-1"
            />
            <Input
              label="Email Mittente"
              type="email"
              value={sesFromEmail}
              onChange={(e) => setSesFromEmail(e.target.value)}
              placeholder="noreply@klaro7.com"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={sesEnabled}
              onChange={(e) => setSesEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
            />
            Abilitato
          </label>
        </div>
      </div>

      {/* SMTP Section */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">SMTP</h3>
              <p className="text-xs text-neutral-600">Configurazione server SMTP per l'invio email</p>
            </div>
          </div>
          <Badge variant={smtpEnabled ? 'success' : 'default'}>
            {smtpEnabled ? 'Attivo' : 'Disattivato'}
          </Badge>
        </div>
        <div className="space-y-4 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Host"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="authsmtp.securemail.pro"
            />
            <Input
              label="Porta"
              type="number"
              value={String(smtpPort)}
              onChange={(e) => setSmtpPort(Number(e.target.value))}
              placeholder="465"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Username"
              value={smtpUsername}
              onChange={(e) => setSmtpUsername(e.target.value)}
              placeholder="info@klaro7.com"
            />
            <Input
              label="Password"
              type="password"
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              placeholder="..."
            />
          </div>
          <Input
            label="Email Mittente"
            type="email"
            value={smtpFromEmail}
            onChange={(e) => setSmtpFromEmail(e.target.value)}
            placeholder="info@klaro7.com"
          />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={smtpSecure}
                onChange={(e) => setSmtpSecure(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
              />
              SSL/TLS
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={smtpEnabled}
                onChange={(e) => setSmtpEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
              />
              Abilitato
            </label>
          </div>
          {/* SMTP Save + Test */}
          <div className="flex items-center justify-between border-t border-neutral-100 pt-4">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={handleTestSmtp}
                loading={smtpTestMutation.isPending}
              >
                Verifica connessione
              </Button>
              {smtpTestResult && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  smtpTestResult.ok
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {smtpTestResult.ok ? (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      OK — Connessione riuscita
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>KO — {smtpTestResult.error}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={handleSaveEmail}
              loading={updateMutation.isPending}
            >
              Salva Impostazioni Email
            </Button>
          </div>
        </div>
      </div>

      <FeatureFlagsSection />
    </div>
  );
}
